<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row class="mb-2">
          <!-- Search -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block"
              style="max-width: 300px;"
              placeholder="Search items..."
            />
          </b-col>

          <!-- Actions -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-md-end justify-content-start flex-wrap mt-2 mt-md-0" style="gap: 10px;">

            <!-- Watch Now Button -->
            <b-button variant="outline-success" @click="openWatchVideo">
              <feather-icon icon="PlayIcon" size="16" class="mr-50" />
              Watch Now
            </b-button>

            <!-- Import Button -->
            <b-button v-if="canAdd" variant="outline-primary" @click="onClickImport">
              <feather-icon icon="UploadIcon" size="16" class="mr-50" />
              Import
            </b-button>

            <!-- Bulk Delete Button -->
            <b-button
              variant="danger"
              @click="onClickBulkDelete"
              v-if="selectedRows && selectedRows.length > 0"
              :disabled="bulkDeleteLoading"
            >
              <b-spinner v-if="bulkDeleteLoading" small class="mr-50" />
              <feather-icon v-else icon="TrashIcon" size="16" class="mr-50" />
              Delete All
            </b-button>

            <b-button v-if="canAdd" variant="primary" @click="$router.push('/apps/fbr-invoice/items/add')">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Add Item
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Select All Summary -->
      <b-alert v-if="selectAll && selectedRows.length > 0" variant="primary" show class="mx-2 mb-2">
        <div class="alert-body font-weight-bold d-flex align-items-center justify-content-between">
          <span>
            <feather-icon icon="InfoIcon" class="mr-50" />
            You have selected all {{ selectedRows.length }} matching items.
          </span>
          <b-button size="sm" variant="outline-primary" @click="selectAll = false; toggleSelectAll()">
            Clear Selection
          </b-button>
        </div>
      </b-alert>

      <b-table
        ref="refItemsListTable"
        class="position-relative"
        :items="fetchItems"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="No matching records found"
        :busy="isTableLoading"
        sticky-header="true"
        :selectable="true"
        @row-selected="onRowSelected"
        select-mode="multi"
      >
        <!-- Loading Skeleton -->
        <template #table-busy>
          <table-skeleton :columns="tableColumns" :rows="5" />
        </template>

        <!-- Custom Header Checkbox for select all -->
        <template #head(select)>
          <b-form-checkbox
            v-model="selectAll"
            class="custom-control-primary"
            @change="toggleSelectAll"
          />
        </template>

        <!-- Column: Select -->
        <template #cell(select)="data">
          <b-form-checkbox
            :checked="data.rowSelected"
            class="custom-control-primary"
            @change="data.toggleDetails(); $refs.refItemsListTable.selectRow(data.index)"
            @input="(val) => { if(val) $refs.refItemsListTable.selectRow(data.index); else $refs.refItemsListTable.unselectRow(data.index); }"
          />
        </template>

        <!-- Column: ID -->
        <template #cell(id)="data">
          <span>{{ data.value }}</span>
        </template>

        <!-- Column: Item Code -->
        <template #cell(itemCode)="data">
          <span
            class="font-weight-bold text-primary cursor-pointer hover-effect"
            @click="viewItem(data.item.id)"
            title="Click to view Item details"
          >
            {{ data.value || '-' }}
          </span>
        </template>

        <!-- Column: Item Name -->
        <template #cell(itemName)="data">
          <div class="d-flex">
            <h6 class="mb-0">{{ data.value }}</h6>
          </div>
        </template>

        <!-- Column: Unit -->
        <template #cell(meta_unit)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Item Group -->
        <template #cell(meta_itemGroup)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Purchase Rate -->
        <template #cell(itemPurchaseRate)="data">
          <span class="text-success">{{ data.value ? `Rs. ${data.value}` : '-' }}</span>
        </template>

        <!-- Column: Trade Price -->
        <template #cell(tradePrice)="data">
          <span class="text-info">{{ data.value ? `Rs. ${data.value}` : '-' }}</span>
        </template>

        <!-- Column: Retail Price -->
        <template #cell(retailPrice)="data">
          <span class="text-warning">{{ data.value ? `Rs. ${data.value}` : '-' }}</span>
        </template>

        <!-- Column: Sales Tax Percent -->
        <template #cell(salesTaxPercent)="data">
          <span class="text-muted">{{ data.value ? `${data.value}%` : '-' }}</span>
        </template>

        <!-- Column: Add Sales Tax Percent -->
        <template #cell(addSalesTaxPercent)="data">
          <span class="text-muted">{{ data.value ? `${data.value}%` : '-' }}</span>
        </template>

        <!-- Column: HS Code -->
        <template #cell(meta_HSCode)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Status -->
        <template #cell(isActive)="data">
          <b-badge :variant="getStatusVariant(data.value)" pill class="status-badge">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <!-- View Button -->
            <b-button
              variant="flat-primary"
              class="btn-icon rounded-circle"
              title="View Item"
              @click="$router.push(`/apps/fbr-invoice/items/view/${data.item.id}`)"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>

            <!-- Edit Button -->
            <b-button
              variant="flat-info"
              class="btn-icon rounded-circle"
              title="Edit Item"
              @click="$router.push(`/apps/fbr-invoice/items/edit/${data.item.id}`)"
            >
              <feather-icon icon="EditIcon" size="16" />
            </b-button>

            <!-- Delete Button -->
            <b-button
              variant="flat-danger"
              class="btn-icon rounded-circle"
              title="Delete Item"
              @click="onDeleteItems(data.item.id)"
            >
              <feather-icon icon="TrashIcon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <!-- Per Page -->
            <label class="mb-0 mr-1">Entries</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              style="width: 80px"
            />
            <span class="text-muted"
              >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>

      <!-- Import Modal -->
      <items-list-import-modal
        :show.sync="isImportModalVisible"
        :loading="importLoading"
        @bulkImport="bulkImport"
      />

    </b-card>
  </div>
</template>

<script>
import store from '@/store';
import { onUnmounted, ref, computed, watch } from '@vue/composition-api';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BSpinner,
  BFormCheckbox,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import itemsStoreModule from './ItemsStoreModule';
import uomStoreModule from '../unit-of-measure/UOMStoreModule';
import itemGroupStoreModule from '../item-group/ItemGroupStoreModule';
import hsCodeStoreModule from '../hs-code/HSCodeStoreModule';
import useItemsList from './useItemsList';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import TableSkeleton from '@/components/common/skeletons/TableSkeleton.vue';
import ItemsListImportModal from './ItemsListImportModal.vue';

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdownItem,
    BPagination,
    BSpinner,
    BFormCheckbox,

    vSelect,
    TableSkeleton,
    ItemsListImportModal,
  },
  setup(props, { root }) {
    const ITEMS_APP_STORE_MODULE_NAME = 'fbr-invoice-items';
    const UOM_APP_STORE_MODULE_NAME = 'fbr-invoice-unit-of-measure';
    const ITEM_GROUP_APP_STORE_MODULE_NAME = 'fbr-invoice-item-group';
    const HS_CODE_APP_STORE_MODULE_NAME = 'fbr-invoice-hs-code';

    // Register modules
    if (!store.hasModule(ITEMS_APP_STORE_MODULE_NAME))
      store.registerModule(ITEMS_APP_STORE_MODULE_NAME, itemsStoreModule);

    if (!store.hasModule(UOM_APP_STORE_MODULE_NAME))
      store.registerModule(UOM_APP_STORE_MODULE_NAME, uomStoreModule);

    if (!store.hasModule(ITEM_GROUP_APP_STORE_MODULE_NAME))
      store.registerModule(ITEM_GROUP_APP_STORE_MODULE_NAME, itemGroupStoreModule);

    if (!store.hasModule(HS_CODE_APP_STORE_MODULE_NAME))
      store.registerModule(HS_CODE_APP_STORE_MODULE_NAME, hsCodeStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(ITEMS_APP_STORE_MODULE_NAME))
        store.unregisterModule(ITEMS_APP_STORE_MODULE_NAME);
      if (store.hasModule(UOM_APP_STORE_MODULE_NAME))
        store.unregisterModule(UOM_APP_STORE_MODULE_NAME);
      if (store.hasModule(ITEM_GROUP_APP_STORE_MODULE_NAME))
        store.unregisterModule(ITEM_GROUP_APP_STORE_MODULE_NAME);
      if (store.hasModule(HS_CODE_APP_STORE_MODULE_NAME))
        store.unregisterModule(HS_CODE_APP_STORE_MODULE_NAME);
    });

    const isExporting = ref(false);
    const isImportModalVisible = ref(false);
    const importLoading = ref(false);

    // Video link for Watch Now button
    const itemsVideoLink = 'https://drive.google.com/file/d/1x-D8GNykFtwv_xTeFexdDmQBXYZV9QkA/view';

    // Watch video function
    const openWatchVideo = () => {
      window.open(itemsVideoLink, '_blank');
    };

    const viewItem = (id) => {
      root.$router.push(`/apps/fbr-invoice/items/view/${id}`);
    };

    const onDeleteItems = async (id) => {
      try {
        // Show confirmation dialog using root.$bvModal
        const confirmed = await new Promise((resolve) => {
          root.$bvModal
            .msgBoxConfirm('Are you sure you want to delete this Item?', {
              title: 'Please Confirm',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'YES',
              cancelTitle: 'NO',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true,
            })
            .then((value) => resolve(value));
        });

        if (!confirmed) return;

        // Call delete action
        const businessID = store.state.user.businessID;
        await store.dispatch('fbr-invoice-items/deleteItem', { businessID, id });

        // Show success toast
        root.$bvToast.toast('Item deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });

        // Refetch data to update the table
        refetchData();
      } catch (error) {
        console.error('Error deleting item:', error);

        // Show user-friendly error message
        let errorMessage = 'Error deleting Item. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    };

    const exportItems = async () => {
      isExporting.value = true;
      try {
        const businessID = store.state.user.businessID;
        const response = await store.dispatch('fbr-invoice-items/exportItems', { businessID });

        // Create download link
        const blob = new Blob([response.data], {
          type:
            response.headers['content-type'] ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `items_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        store.dispatch('app/showToast', {
          title: 'Export successful!',
          text: 'Items exported successfully',
          variant: 'success',
        });
      } catch (error) {
        console.error('Export error:', error);
        store.dispatch('app/showToast', {
          title: 'Export failed',
          text: 'Failed to export items',
          variant: 'danger',
        });
      } finally {
        isExporting.value = false;
      }
    };

    const onClickImport = () => {
      isImportModalVisible.value = true;
    };

    const bulkImport = async (data, onComplete) => {
      importLoading.value = true;

      try {
        const payload = data;
        
        const response = await store.dispatch('fbr-invoice-items/bulkUploadItems', payload);

        const responseData = response?.data || {};
        const innerData = responseData?.data || {};
        
        const hasValidationErrors = innerData?.errors && innerData.errors.length > 0;
        const errorCount = innerData?.errorCount || 0;

        if (!hasValidationErrors && errorCount === 0 && responseData.status !== 'error') {
          root.$bvToast.toast(`Successfully imported items`, {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
        }

        refetchData();

        if (onComplete) {
          onComplete(true, response);
        }
      } catch (error) {
        console.error('Bulk import error:', error);
        
        const errMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : error.message || 'Failed to import items';

        root.$bvToast.toast(errMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });

        if (onComplete) {
          onComplete(false, errMessage);
        }
      } finally {
        importLoading.value = false;
      }
    };

    // Multi-select and bulk delete references
    const bulkDeleteLoading = ref(false);

    const onRowSelected = (rows) => {
      // If selectAll is active, we don't want local page navigation to override our global selection
      if (selectAll.value) return;

      selectedRows.value = rows;
    };

    const onClickBulkDelete = async () => {
      if (!selectedRows.value || selectedRows.value.length === 0) return;

      const confirmed = await root.$bvModal.msgBoxConfirm(`Are you sure you want to delete ${selectedRows.value.length} selected items?`, {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });

      if (!confirmed) return;

      bulkDeleteLoading.value = true;
      try {
        const businessID = store.state.user.businessID;
        const ids = selectedRows.value.map(row => row.id).join(',');
        await store.dispatch('fbr-invoice-items/deleteItem', { businessID, id: ids });
        
        root.$bvToast.toast('Items deleted successfully', { title: 'Success', variant: 'success', solid: true });
        refetchData();
      } catch (error) {
        root.$bvToast.toast('Error deleting items', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        bulkDeleteLoading.value = false;
        selectAll.value = false;
      }
    };

    const {
      fetchItems,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      perPageOptions,
      searchQuery: composableSearchQuery,
      refItemsListTable,
      refetchData,
      isTableLoading,
      selectedRows,
      selectAll,
      toggleSelectAll,
    } = useItemsList();

    // Local search query for debouncing
    const searchQuery = ref('');

    // Watch local search query and sync with composable
    watch(searchQuery, (newValue) => {
      composableSearchQuery.value = newValue;
    });

    // Helper function for status variant
    const getStatusVariant = (isActive) => {
      return isActive ? 'light-success' : 'light-danger';
    };

    // Compute data meta for pagination
    const dataMeta = computed(() => {
      const from = (currentPage.value - 1) * perPage.value + 1;
      const to = Math.min(currentPage.value * perPage.value, totalItems.value);
      const of = totalItems.value;
      return { from, to, of };
    });

    // Check user permissions dynamically
    const canAdd = computed(() => {
      const authRoutes = store.state.user?.authRoutes || {};
      const permissions = authRoutes['items'];
      if (permissions && permissions.add !== undefined) {
        return permissions.add === true;
      }
      return true; // Default fallback if no permissions explicitly restricted
    });

    return {
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      refItemsListTable,

      fetchItems,
      refetchData,
      viewItem,
      onDeleteItems,
      exportItems,
      isExporting,
      isTableLoading,

      canAdd,
      onClickImport,
      bulkImport,
      isImportModalVisible,
      importLoading,

      // Helper functions
      getStatusVariant,
      openWatchVideo,

      // Multi-select & Bulk Delete
      selectedRows,
      selectAll,
      bulkDeleteLoading,
      onRowSelected,
      toggleSelectAll,
      onClickBulkDelete,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// Skeleton loader styles
.skeleton-row {
  .skeleton-cell {
    padding: 0.75rem;

    .skeleton-item {
      height: 20px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 4px;
    }
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Enhanced badge styling for status
.status-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  transition: all 0.2s ease-in-out;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

// Light variant badge styles
.badge.badge-pill {
  &.light-success {
    background-color: #d1f2eb;
    color: #0f5132;
    border: 1px solid #badbcc;
  }

  &.light-danger {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
}

// Table sticky header styling
.b-table-sticky-header {
  max-height: 80vh !important;
}

// Clickable code styling
.cursor-pointer {
  cursor: pointer;
}

.hover-effect {
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0056b3 !important;
    text-decoration: underline;
    transform: translateY(-1px);
  }
}

.hover-effect {
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0056b3 !important;
    text-decoration: underline;
    transform: translateY(-1px);
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
